import { ReactNode, useMemo, useState } from 'react';
import {
  Box,
  Button,
  DataTable,
  DataTableBody,
  Disclosure,
  DisclosureContent,
  DisclosureTrigger,
  Field,
  Flex,
  Spinner,
  Text,
} from '@optiaxiom/react';
import { IconCodeBranch, IconCopy, IconXmark } from '@optimizely/axiom-icons';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';

import { ArtifactContainer, ArtifactProvider, Artifacts } from '@frontend/components/Artifact';
import { ConfirmationModal } from '@frontend/components/confirmationModal/components/ConfirmationModal';
import { MarkdownText } from '@frontend/components/MarkdownText';
import { AgentExecutionItem } from '@frontend/types/agentExecution';
import { StepExecution } from '@frontend/types/stepExecution';
import { copyToClipboard } from '@frontend/utils/copyToClipboard';

import {
  useExecutionDetails,
  useMemoryChunks,
  useMemoryDetails,
} from '../../hooks/useAgentExecutions';
import { flattenArtifacts } from '../../utils/artifactUtils';
import { formatExecutionTime } from '../../utils/dateUtils';
import { getTotalTokenUsageForWorkflow } from '../../utils/executionUtils';
import { TokenUsageSection } from '../shared/TokenUsageSection';
import { EmptyStateMessage } from '../specialized-agent/shared/EmptyStateMessage';
import { ExecutionOutputDisplay } from '../specialized-agent/shared/ExecutionOutputDisplay';
import { ExecutionStatusBadge } from '../specialized-agent/shared/ExecutionStatusBadge';
import { MemoryChunksDisplay } from '../specialized-agent/shared/MemoryChunksDisplay';

import { styles } from './ExecutionDetailsSidebar.css';

type ExecutionDetailsSidebarProps = {
  execution: AgentExecutionItem;
  stepExecution?: StepExecution;
  onClose: () => void;
  onCancel?: () => void;
  isCancelling?: boolean;
  onResume?: () => void;
  isResuming?: boolean;
};

export function ExecutionDetailsSidebar({
  execution,
  onClose,
  onCancel,
  isCancelling = false,
  onResume,
  isResuming = false,
}: ExecutionDetailsSidebarProps) {
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [showResumeConfirmation, setShowResumeConfirmation] = useState(false);
  // Determine if we should poll based on execution status (treat pending as running)
  const shouldPoll =
    execution.status.toLowerCase() === 'pending' || execution.status.toLowerCase() === 'running';

  const { data: details, isPending: isLoadingDetails } = useExecutionDetails(
    execution.links.self,
    shouldPoll
  );

  // Also poll memory if execution details show pending/running status
  const shouldPollMemory =
    shouldPoll ||
    details?.status?.toLowerCase() === 'pending' ||
    details?.status?.toLowerCase() === 'running';

  const { data: memory, isPending: isLoadingMemory } = useMemoryDetails(
    details?.links?.memory,
    shouldPollMemory
  );

  const { data: memoryChunks, isPending: isLoadingChunks } = useMemoryChunks(
    memory?.links?.chunks,
    shouldPollMemory
  );

  const formatJsonForMarkdown = (obj: unknown): string => {
    return '```json\n' + JSON.stringify(obj, null, 2) + '\n```';
  };

  const canCancel =
    details?.status?.toLowerCase() === 'pending' ||
    details?.status?.toLowerCase() === 'running' ||
    isCancelling;

  const canResume = details?.status?.toLowerCase() === 'cancelled' && !isResuming;

  type ExecutionDetailRow = {
    field: string | ReactNode;
    value: string | ReactNode;
  };

  const columnHelper = createColumnHelper<ExecutionDetailRow>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('field', {
        header: 'Field',
        size: 120,
        cell: info => <Text fontWeight="500">{info.getValue()}</Text>,
      }),
      columnHelper.accessor('value', {
        header: 'Value',
        cell: info => info.getValue(),
      }),
    ],
    [columnHelper]
  );

  const executionDetailsData = useMemo(() => {
    if (!details) return [];

    const rows: ExecutionDetailRow[] = [
      {
        field: 'Agent Name',
        value: (
          <Flex flexDirection="row" gap="8" alignItems="center" w="full">
            <Text truncate flex="1">
              {`${details.workflow_agent.name}`}
            </Text>
            <Button
              appearance="subtle"
              size="sm"
              icon={<IconCopy />}
              onClick={() => copyToClipboard(details.workflow_agent?.name || 'Unknown')}
              aria-label="Copy agent name"
            />
          </Flex>
        ),
      },
      {
        field: 'Execution ID',
        value: (
          <Flex flexDirection="row" gap="8" alignItems="center" w="full">
            <Text truncate flex="1">
              {details.id}
            </Text>
            <Button
              appearance="subtle"
              size="sm"
              icon={<IconCopy />}
              onClick={() => copyToClipboard(details.id)}
              aria-label="Copy execution ID"
            />
          </Flex>
        ),
      },
      {
        field: 'Execution Time',
        value: formatExecutionTime(details.start_time),
      },
    ];

    if (details.result?.execution_time) {
      rows.push({
        field: 'Duration',
        value: `${details.result.execution_time.toFixed(2)}s`,
      });
    }

    return rows;
  }, [details]);

  const table = useReactTable({
    data: executionDetailsData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: { pagination: { pageIndex: 0, pageSize: 10 } },
  });

  const handleCancelClick = () => {
    setShowCancelConfirmation(true);
  };

  const handleConfirmCancel = () => {
    onCancel?.();
    setShowCancelConfirmation(false);
  };

  const handleCancelDialogClose = () => {
    setShowCancelConfirmation(false);
  };

  const handleResumeClick = () => {
    setShowResumeConfirmation(true);
  };

  const handleConfirmResume = () => {
    onResume?.();
    setShowResumeConfirmation(false);
  };

  const handleResumeDialogClose = () => {
    setShowResumeConfirmation(false);
  };

  return (
    <ArtifactProvider>
      <ArtifactContainer>
        <Flex flexDirection="column" gap="16" px="16" pb="16">
          <Flex
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            className={styles.header}
          >
            <Flex flexDirection="row" alignItems="center" gap="12">
              <Box className={styles.iconContainer}>
                <IconCodeBranch size="20" />
              </Box>
              <Text fontWeight="600" fontSize="lg">
                Workflow Execution
              </Text>
              {details && <ExecutionStatusBadge status={details.status} showAsRunning />}
            </Flex>
            <Flex flexDirection="row" gap="8" alignItems="center">
              {canCancel && (
                <Button
                  appearance="danger"
                  onClick={handleCancelClick}
                  disabled={isCancelling}
                  loading={isCancelling}
                  aria-label="Cancel workflow execution"
                >
                  Cancel Execution
                </Button>
              )}
              {canResume && (
                <Button
                  appearance="primary"
                  onClick={handleResumeClick}
                  disabled={isResuming}
                  loading={isResuming}
                  aria-label="Resume workflow execution"
                >
                  Resume Execution
                </Button>
              )}
              <Button
                icon={<IconXmark size="16" />}
                appearance="subtle"
                onClick={onClose}
                aria-label="Close sidebar"
              />
            </Flex>
          </Flex>
          {isLoadingDetails ? (
            <Flex justifyContent="center" py="24">
              <Spinner>Loading details...</Spinner>
            </Flex>
          ) : details ? (
            <>
              {/* Basic Info */}
              <DataTable table={table}>
                <DataTableBody />
              </DataTable>
              {/* Payload */}
              <Disclosure defaultOpen>
                <DisclosureTrigger>
                  <Field label="Input Variables" info="Trigger data that initiated the execution" />
                </DisclosureTrigger>
                <DisclosureContent>
                  <Flex flexDirection="column" gap="16" className={styles.horizontalPadding}>
                    {details.trigger_info?.payload ? (
                      <Field label="Variables">
                        <Box border="1" borderColor="border.default" rounded="md">
                          <Box bg="bg.default" p="12" rounded="md">
                            <MarkdownText
                              text={formatJsonForMarkdown(details.trigger_info.payload)}
                            />
                          </Box>
                        </Box>
                      </Field>
                    ) : details.payload ? (
                      <Field label="Variables">
                        <Box border="1" borderColor="border.default" rounded="md">
                          <Box bg="bg.default" p="12" rounded="md">
                            <MarkdownText text={formatJsonForMarkdown(details.payload)} />
                          </Box>
                        </Box>
                      </Field>
                    ) : (
                      <EmptyStateMessage message="No trigger payload data available" />
                    )}
                  </Flex>
                </DisclosureContent>
              </Disclosure>
              {/* Artifacts - Flattened from all step executions */}
              {details.step_executions &&
                details.step_executions.length > 0 &&
                (() => {
                  const allArtifacts = flattenArtifacts(details.step_executions);

                  return allArtifacts.length > 0 ? (
                    <Disclosure defaultOpen>
                      <DisclosureTrigger>
                        <Field
                          label="Artifacts"
                          info={`${allArtifacts.length} artifact(s) generated during workflow execution`}
                        />
                      </DisclosureTrigger>
                      <DisclosureContent>
                        <Flex flexDirection="column" gap="16" className={styles.horizontalPadding}>
                          <Artifacts artifacts={allArtifacts} />
                        </Flex>
                      </DisclosureContent>
                    </Disclosure>
                  ) : null;
                })()}
              {/* Result */}
              <Disclosure defaultOpen>
                <DisclosureTrigger>
                  <Field label="Output" info="Execution results and response" />
                </DisclosureTrigger>
                <DisclosureContent>
                  <Flex flexDirection="column" gap="16" className={styles.horizontalPadding}>
                    <Field label="Execution Response">
                      <ExecutionOutputDisplay
                        result={details.result ?? null}
                        showStatus={false}
                        emptyMessage="No output available."
                        emptySubMessage={
                          details.status?.toLowerCase() === 'pending' ||
                          details.status?.toLowerCase() === 'running'
                            ? 'Execution in progress.'
                            : 'Execution did not produce any results.'
                        }
                      />
                    </Field>
                  </Flex>
                </DisclosureContent>
              </Disclosure>

              {/* Error */}
              {details.error_message && (
                <Disclosure defaultOpen>
                  <DisclosureTrigger>
                    <Field label="Error" info="Execution error details" />
                  </DisclosureTrigger>
                  <DisclosureContent>
                    <Flex flexDirection="column" gap="16" className={styles.horizontalPadding}>
                      <Field label="Error Details">
                        <Box border="1" borderColor="border.error" rounded="md">
                          <Flex flexDirection="column">
                            <Box bg="bg.error.subtle" p="12" className={styles.roundedTop}>
                              <Text fontSize="sm" fontWeight="600" color="fg.error">
                                Execution Failed
                              </Text>
                            </Box>
                            <Box bg="bg.default" p="12" className={styles.roundedBottom}>
                              <Text fontSize="md" color="fg.error">
                                {details.error_message}
                              </Text>
                            </Box>
                          </Flex>
                        </Box>
                      </Field>
                    </Flex>
                  </DisclosureContent>
                </Disclosure>
              )}

              {/* Execution Memory */}
              {details.links?.memory && (
                <Disclosure>
                  <DisclosureTrigger>
                    <Field label="Execution Memory" info="Agent memory chunks from execution" />
                  </DisclosureTrigger>
                  <DisclosureContent>
                    <Flex className={styles.horizontalPadding}>
                      <MemoryChunksDisplay
                        memoryChunks={memoryChunks?.slice(3) || []}
                        isLoading={isLoadingMemory || isLoadingChunks}
                        emptyMessage="No memory chunks available"
                        emptySubMessage="Memory data will appear here when available."
                        chunkCountLabel="Memory chunks"
                      />
                    </Flex>
                  </DisclosureContent>
                </Disclosure>
              )}

              {/* Token Usage */}
              <TokenUsageSection
                tokenUsage={
                  details.step_executions && details.step_executions.length > 0
                    ? getTotalTokenUsageForWorkflow(details)
                    : details.result?.token_usage
                }
              />
            </>
          ) : (
            <Text color="fg.secondary">Failed to load execution details</Text>
          )}

          <ConfirmationModal
            isShown={showCancelConfirmation}
            title="Cancel Workflow Execution"
            action="Cancel Execution"
            appearance="danger"
            onCancel={handleCancelDialogClose}
            onConfirm={handleConfirmCancel}
          >
            <Text>
              Are you sure you want to cancel this workflow execution? This action cannot be undone.
            </Text>
          </ConfirmationModal>

          <ConfirmationModal
            isShown={showResumeConfirmation}
            title="Resume Workflow Execution"
            action="Resume Execution"
            appearance="primary"
            onCancel={handleResumeDialogClose}
            onConfirm={handleConfirmResume}
          >
            <Text>
              Resume this cancelled workflow execution from where it left off? The workflow will
              continue from the next step after the last completed step.
            </Text>
          </ConfirmationModal>
        </Flex>
      </ArtifactContainer>
    </ArtifactProvider>
  );
}
